import { forwardRef, useRef, useState, type KeyboardEvent } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  hasAsterisk,
  type Indicator,
  type IndicatorResponse,
} from '@/lib/schema/types';
import { useObservation } from '@/lib/state/useObservation';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const TRUNCATE_AT = 160;

type Choice = { value: IndicatorResponse; label: string; tone: Tone };

export function IndicatorRow({
  itemId,
  indicator,
}: {
  itemId: string;
  indicator: Indicator;
}) {
  const { state, setResponse } = useObservation();
  const value = state.responses[indicator.id];

  /**
   * The manual prints scoring rules ("Observe two different examples for two
   * different children", "NA permitted if only infants are present") as italic
   * lines *after* the indicator sentence. On screen that buried them: the
   * sentence is long, so the rule fell past the truncation point and observers
   * never saw it. We lift them in front of the sentence instead, where they
   * survive truncation and read as the precondition they are.
   */
  const rules = [
    ...new Set(
      [indicator.observe_instruction, indicator.na_condition]
        .map((r) => (r ?? '').trim().replace(/\.$/, ''))
        .filter(Boolean),
    ),
  ];

  const bodyText =
    indicator.text +
    (indicator.example_text ? ` (Ex: ${indicator.example_text})` : '');

  const needsReadMore = bodyText.length > TRUNCATE_AT;

  const choices: Choice[] = [
    { value: 'Y', label: 'Yes', tone: 'yes' },
    { value: 'N', label: 'No', tone: 'no' },
    ...(indicator.allows_na
      ? [{ value: 'NA' as const, label: 'Not applicable', tone: 'na' as const }]
      : []),
  ];

  const toggle = (next: IndicatorResponse) => {
    setResponse(itemId, indicator.id, value === next ? null : next);
  };

  return (
    <div className="flex items-start gap-2 rounded-md p-2 transition hover:bg-background/60">
      <div className="flex-1 pt-1 text-sm leading-snug text-foreground/90">
        <span className="font-semibold tabular-nums">{indicator.code}</span>{' '}
        <IndicatorText
          text={bodyText}
          rules={rules}
          truncated={needsReadMore}
          asterisk={hasAsterisk(indicator)}
          code={indicator.code}
        />
      </div>
      <ResponseRadioGroup
        indicator={indicator}
        choices={choices}
        value={value}
        onToggle={toggle}
      />
    </div>
  );
}

/**
 * WAI-ARIA radiogroup with roving tabindex:
 *   - Only the "checked" (or first) option is in the tab order.
 *   - Arrow keys cycle; Space/Enter (re-)commit the focused choice.
 *   - 44×44 touch targets (Apple HIG / WCAG 2.5.5 minimum).
 *   - Clicking the active choice again clears it (toggle semantics preserved).
 */
function ResponseRadioGroup({
  indicator,
  choices,
  value,
  onToggle,
}: {
  indicator: Indicator;
  choices: Choice[];
  value: IndicatorResponse | undefined;
  onToggle: (next: IndicatorResponse) => void;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const activeIdx = value ? choices.findIndex((c) => c.value === value) : -1;
  // The tab stop is the active choice (or the first button if nothing is
  // selected yet). Arrow keys move focus but don't change the tab stop,
  // which is the canonical WAI-ARIA radiogroup pattern.
  const tabStopIdx = activeIdx >= 0 ? activeIdx : 0;

  const focus = (nextIdx: number) => {
    refs.current[nextIdx]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent, idx: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown': {
        e.preventDefault();
        focus((idx + 1) % choices.length);
        break;
      }
      case 'ArrowLeft':
      case 'ArrowUp': {
        e.preventDefault();
        focus((idx - 1 + choices.length) % choices.length);
        break;
      }
      case 'Home': {
        e.preventDefault();
        focus(0);
        break;
      }
      case 'End': {
        e.preventDefault();
        focus(choices.length - 1);
        break;
      }
      case ' ':
      case 'Enter': {
        e.preventDefault();
        onToggle(choices[idx]!.value);
        break;
      }
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={`Score for indicator ${indicator.code}`}
      className="flex flex-shrink-0 gap-1"
    >
      {choices.map((choice, idx) => {
        const active = value === choice.value;
        return (
          <ResponseButton
            key={choice.value}
            ref={(el) => {
              refs.current[idx] = el;
            }}
            label={choice.label}
            active={active}
            tabIndex={tabStopIdx === idx ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, idx)}
            onClick={() => onToggle(choice.value)}
            tone={choice.tone}
          >
            {choice.tone === 'yes' ? (
              <Check className="h-4 w-4" />
            ) : choice.tone === 'no' ? (
              <X className="h-4 w-4" />
            ) : (
              <span className="text-[11px] font-semibold">NA</span>
            )}
          </ResponseButton>
        );
      })}
    </div>
  );
}

type Tone = 'yes' | 'no' | 'na';

interface ResponseButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  tabIndex: number;
  tone: Tone;
  children: React.ReactNode;
}

const ResponseButton = forwardRef<HTMLButtonElement, ResponseButtonProps>(
  function ResponseButton(
    { label, active, onClick, onKeyDown, tabIndex, tone, children },
    ref,
  ) {
    const toneClass =
      tone === 'yes'
        ? active
          ? 'bg-rating-5 text-white border-rating-5'
          : 'hover:bg-rating-5/10 hover:border-rating-5 text-rating-5'
        : tone === 'no'
          ? active
            ? 'bg-rating-1 text-white border-rating-1'
            : 'hover:bg-rating-1/10 hover:border-rating-1 text-rating-1'
          : active
            ? 'bg-muted-foreground text-white border-muted-foreground'
            : 'hover:bg-muted-foreground/10 hover:border-muted-foreground text-muted-foreground';
    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={active}
        aria-label={label}
        tabIndex={tabIndex}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className={cn(
          // 44×44 touch target per WCAG 2.5.5; inner icon kept visually
          // compact via flex-center so the wireframe footprint is preserved
          // while meeting the touch-accessibility minimum.
          'inline-flex h-11 w-11 items-center justify-center rounded-md border border-input bg-background transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          toneClass,
        )}
      >
        {children}
      </button>
    );
  },
);

function IndicatorText({
  text,
  rules,
  truncated,
  asterisk,
  code,
}: {
  text: string;
  rules: string[];
  truncated: boolean;
  asterisk: boolean;
  code: string;
}) {
  const [open, setOpen] = useState(false);
  const rulePrefix =
    rules.length > 0 ? (
      <span className="font-medium italic text-dece-navy">
        ({rules.join('; ')}){' '}
      </span>
    ) : null;
  if (!truncated) {
    return (
      <span>
        {rulePrefix}
        {text}
        {asterisk ? '*' : ''}
      </span>
    );
  }
  const short = text.slice(0, TRUNCATE_AT).replace(/\s+\S*$/, '') + '… ';
  return (
    <>
      <span>
        {rulePrefix}
        {short}
        {asterisk ? '* ' : ''}
      </span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            className="text-dece-sky underline-offset-2 hover:underline"
          >
            Read More
          </button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Indicator {code}</DialogTitle>
          </DialogHeader>
          <p className="text-sm leading-relaxed">
            {rulePrefix}
            {text}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
